import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    List,
    Stack,
    styled,
    TextField,
    Theme,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import PlusCircle from '../resources/images/aq-plus-circle.svg';
import BinIcon from '../resources/images/bin.svg';
import CollapseIcon from '../resources/images/collapse_icon.svg';
import DisabledPlusCircle from '../resources/images/disabled-plus-circle.svg';
import ExpandIcon from '../resources/images/expand_icon.svg';

export interface FailedMetric {
    default_threshold: number;
    thresholds: { [k: string]: number };
}

const makeClasses = makeStyles((theme: Theme) => ({
    dataGrid: {
        border: 'unset',
        '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.background.default,
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'unset',
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: theme.palette.background.default,
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'unset',
            cursor: 'text',
        },
        '& .MuiDataGrid-cell': {
            borderBottom: 'unset',
        },
        '& .MuiDataGrid-columnsContainer': {
            borderBottom: 'unset',
        },
        '& .MuiDataGrid-columnSeparator': {
            display: 'none',
        },
        '& .MuiDataGrid-row': {
            backgroundColor: 'unset',
        },
    },
    description: { color: 'black' },
    tableCellContainer: {
        width: '90%',
        height: 38,
        padding: 5,
        border: '1px solid #dadcdf',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
    },
}));

// wrapper for backwards compatability
const FailedMetricEditor = (props: { val: any; setVal: (val: any) => void; name: string; description: string }) => {
    const { val, setVal, name, description } = props;

    const fm: FailedMetric =
        typeof val === 'number'
            ? {
                  default_threshold: val,
                  thresholds: {},
              }
            : {
                  default_threshold: val.default_threshold ?? val.defaultThreshold,
                  thresholds: val.thresholds,
              };

    return <FailedMetricEditorInner failedMetric={fm} setFailedMetric={setVal} name={name} description={description} />;
};

// input validation utils
const validThreshold = (val?: number) => {
    if (val) {
        if (val > 0) {
            if (val % 1 === 0) {
                return true;
            }
        }
    }
    return false;
};

const validName = (val?: string) => !!(val && val.length > 0);

const thresholdMessage = (threshold: number) => {
    if (!threshold) {
        return 'Please enter a threshold';
    }
    if (threshold <= 0) {
        return 'Threshold must be positive';
    }
    if (threshold % 1 !== 0) {
        return 'Please enter whole number';
    }
    return undefined;
};

const CleanField = styled(TextField)((theme) => ({
    '& fieldset': {
        borderRadius: 7,
    },
}));

const FailedMetricEditorInner = (props: {
    failedMetric: FailedMetric;
    setFailedMetric: (fm: FailedMetric) => void;
    name;
    description;
}) => {
    const { failedMetric, setFailedMetric, name, description } = props;
    const classes = makeClasses();

    const [editorOpen, setEditorOpen] = useState(Object.keys(failedMetric.thresholds).length > 0);
    const [editedRow, setEditedRow] = useState<{ name: string; threshold: number }>({ name: '', threshold: 0 });

    const [showValidations, setShowValidations] = useState(false);

    const canAddRow = () => {
        if (showValidations) {
            return validName(editedRow.name) && validThreshold(editedRow.threshold);
        }
        return false;
    };

    const tryAddRow = () => {
        if (canAddRow()) {
            setFailedMetric({
                ...failedMetric,
                thresholds: { ...failedMetric.thresholds, [editedRow.name]: editedRow.threshold },
            });
            setShowValidations(false);
            setEditedRow({ name: '', threshold: 0 });
        }
    };

    return (
        <ClickAwayListener
            onClickAway={() => {
                setEditedRow({ name: '', threshold: 0 });
                setEditorOpen(false);
                tryAddRow();
            }}
        >
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                        <Stack className="ItemDetailsCol1">
                            <Stack direction="column" width="385px">
                                <Typography style={{ marginRight: '3px', marginBottom: '10px' }} variant="body2">
                                    {name}
                                </Typography>
                                <Typography variant="body1" className="DescriptionContainer">
                                    {description}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack
                            direction="row"
                            className="ItemDetailsCol2"
                            style={{ flexDirection: 'row', justifyContent: 'start' }}
                        >
                            <Box style={{ width: '100px' }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={failedMetric.default_threshold}
                                    onChange={(e) =>
                                        setFailedMetric({ ...failedMetric, default_threshold: +e.target.value })
                                    }
                                />
                            </Box>
                            <Button
                                disabled={editorOpen}
                                onClick={() => setEditorOpen(true)}
                                style={{ marginLeft: 20 }}
                            >
                                <img
                                    src={editorOpen ? DisabledPlusCircle : PlusCircle}
                                    alt="open-editor"
                                    style={{ marginRight: 8 }}
                                />
                                Add Customized Threshold
                            </Button>
                            <Stack
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flex: '1',
                                    justifyContent: 'end',
                                    marginTop: '10px',
                                    marginRight: '20px',
                                }}
                            >
                                <img
                                    src={editorOpen ? CollapseIcon : ExpandIcon}
                                    alt="expand"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setEditorOpen(!editorOpen)}
                                />
                            </Stack>
                        </Stack>
                    </div>
                </div>

                {editorOpen ? (
                    <Box width="100%" padding="0px 16px 20px">
                        <Divider style={{ marginBottom: 20, marginTop: 10 }} />
                        <Typography className={classes.description}>
                            Define below customized threshold for specific product type. Not specified product will use
                            the default value.
                        </Typography>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                marginTop: 12,
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: 50 }}>
                                <Typography style={{ marginBottom: 15, color: '#818183' }}>Product Type</Typography>
                                <CleanField
                                    variant="outlined"
                                    size="small"
                                    value={editedRow.name}
                                    type="text"
                                    onChange={(e) => {
                                        setShowValidations(true);
                                        setEditedRow({ ...editedRow, name: e.target.value });
                                    }}
                                    error={showValidations ? !validName(editedRow.name) : null}
                                    required
                                    helperText={
                                        showValidations && !validName(editedRow.name)
                                            ? 'Please enter product type name'
                                            : null
                                    }
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography style={{ marginBottom: 15, color: '#818183' }}>Threshold</Typography>
                                <CleanField
                                    variant="outlined"
                                    size="small"
                                    value={editedRow.threshold}
                                    type="number"
                                    onChange={(e) => {
                                        setShowValidations(true);
                                        setEditedRow({ ...editedRow, threshold: +e.target.value });
                                    }}
                                    error={showValidations ? !validThreshold(editedRow.threshold) : null}
                                    helperText={showValidations ? thresholdMessage(editedRow.threshold) : null}
                                />
                            </div>

                            <Button
                                style={{ marginTop: '35px', marginLeft: '5px' }}
                                disabled={!canAddRow()}
                                onClick={tryAddRow}
                            >
                                Add
                            </Button>
                        </div>

                        <Divider style={{ marginTop: 20, marginBottom: 20 }} />
                        {Object.keys(failedMetric.thresholds).length === 0 && (
                            <Typography> No customized threshold </Typography>
                        )}
                        {Object.keys(failedMetric.thresholds).length > 0 && (
                            <Stack>
                                <Typography>
                                    {Object.keys(failedMetric.thresholds).length} customized threshold:
                                </Typography>
                                <List style={{ width: '65' }}>
                                    {Object.keys(failedMetric.thresholds).map((k) => (
                                        <div
                                            key={k}
                                            style={{
                                                display: 'flex',
                                                width: '50%',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                paddingBottom: 5,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 5,
                                                    height: 5,
                                                    border: '5px solid black',
                                                    borderRadius: '100%',
                                                    marginRight: 10,
                                                }}
                                            />
                                            <Typography style={{ marginRight: 10 }}> {k} </Typography>
                                            <Typography style={{ marginRight: 10 }}> {'>'} </Typography>
                                            <Typography style={{ marginRight: 10 }}>
                                                {' '}
                                                {failedMetric.thresholds[k]}{' '}
                                            </Typography>
                                            <img
                                                src={BinIcon}
                                                alt="remove-item"
                                                style={{ cursor: 'pointer', marginLeft: '10px' }}
                                                onClick={() => {
                                                    const ts = failedMetric.thresholds;
                                                    delete ts[k];
                                                    setFailedMetric({ ...failedMetric, thresholds: ts });
                                                }}
                                            />
                                        </div>
                                    ))}
                                </List>
                            </Stack>
                        )}
                    </Box>
                ) : null}
            </div>
        </ClickAwayListener>
    );
};

export default FailedMetricEditor;
